import { Show } from '@/components/admin/show';
import { RecordField } from '@/components/admin/record-field';
import { Separator } from '@/components/ui/separator';
import { ColonnesDaffichage } from './ColonnesDaffichage';
import { SecretDappel } from './SecretDappel';
import { Exports } from './Exports';
import { DesignationDunOrgadmin } from './DesignationDunOrgadmin';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * **LA FICHE D'UNE STRUCTURE — F-56, ET SON OUVERTURE LAISSE UNE TRACE.**
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * F-56 : « **l'ouverture de la fiche d'une structure laisse une ligne de trace**, patron de
 * la fiche d'un compte. » C'est le serveur qui l'écrit, et c'est la raison pour laquelle
 * **rien ne précharge cette fiche** : un survol qui l'ouvrirait « pour aller plus vite »
 * écrirait des lignes que personne n'a demandées.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **DEUX RÔLES, DEUX OBJETS — ET L'ÉCRAN LIT CE QU'IL A REÇU.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * §11 : « **la forme de réponse dépend du rôle, et c'est la matrice qui l'exige.** Une
 * ressource lue par un `orgadmin` et par le compte qui voit tout **rend deux objets
 * différents**, pas un objet unique avec des conditions à l'intérieur. **Une condition
 * enfouie dans un service ne se relit jamais en revue** — c'est le motif entier de la
 * matrice. »
 *
 * Le contrat l'encode par un `oneOf` discriminé sur `role` : le `superadmin` reçoit huit
 * champs, l'`orgadmin` en reçoit quatre. **Cet écran ne teste donc aucun rôle** : chaque bloc
 * se rend si le champ qu'il montre est là. Tester le rôle ici ferait **une troisième copie de
 * la même règle** — après `@Allow()` et la matrice —, et c'est celle qu'on oublierait.
 *
 * §7 le dit du champ le plus sensible : « **champs interdits en réponse à un `orgadmin`** :
 * les **six colonnes d'affichage** et tout ce qui en dérive, **`paraitSurLeSite` compris**.
 * **Champs interdits aux deux** : l'**empreinte du secret d'appel**, sans exception.
 * **`secretEmis` est un booléen dérivé, jamais l'empreinte.** »
 *
 * ───────────────────────────────────────────────────────────────────────────
 * **CE QUE LA FICHE NE PORTE PAS, ET LA LISTE EST FERMÉE.**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * F-56 : « **la liste des champs d'une structure est FERMÉE, et elle ne gagne rien**
 * (Q540). » Donc : **aucun champ administratif** — « ni numéro d'identification, ni raison
 * sociale, ni adresse postale » —, **aucune personne de contact** — « c'est la décision la
 * plus lourde des six : ce serait une **donnée de personne entrant dans le produit sans
 * compte**, la brèche exacte que la chaîne étanche refuse partout » —, et **aucune note
 * libre** : « c'est le champ où finit par entrer tout ce qui n'a pas de champ, données de
 * personnes comprises, **sans classification et sans durée**. »
 *
 * **Et aucun bouton de suppression**, sur aucun des deux rôles : F-56 (Q543), « **IL
 * N'EXISTE AUCUN GESTE DE SUPPRESSION D'UNE STRUCTURE** ». La matrice le ferme, et le
 * fournisseur de données lève — deux verrous, le second parce que le premier ne fait que
 * cacher.
 *
 * **Aucun état de clientèle non plus**, ni actif, ni éteint : Q539, et le coût est assumé en
 * F-68 — « **une structure vidée et une structure vivante ne se distinguent pas dans le
 * produit.** »
 */
export function FicheDeStructure() {
  return (
    <Show>
      <div className="flex flex-col gap-6">
        <section className="flex flex-col gap-2">
          <RecordField source="nomInterne" label="Nom interne" />
          <RecordField
            source="creeLe"
            label="Créée le"
            render={(structure: { creeLe: string }) =>
              new Date(structure.creeLe).toLocaleString('fr-FR')
            }
          />
        </section>

        <Separator />
        {/* **F-02 — désigner la personne qui tient cet espace.** Le geste porte sur la
            structure de la fiche ouverte, jamais sur la structure courante d'un sélecteur :
            même règle que les exports, on agit sur ce qu'on regarde. Le bloc ne paraît qu'au
            compte qui voit tout — « une structure ne peut pas s'en ajouter un elle-même ». */}
        <DesignationDunOrgadmin />

        <Separator />
        <SecretDappel />

        <Separator />
        <ColonnesDaffichage />

        <Separator />
        {/* Les exports portent sur la structure de la fiche ouverte, jamais sur la structure
            courante d'un sélecteur : on exporte ce qu'on regarde. */}
        <Exports />
      </div>
    </Show>
  );
}
